/** Loppu on yle-boilerplatea */

/**	
@class YleInteractiveEmbed
@classdesc A class representing an interactive app that can be mounted to the DOM.
@property {string} appName - The name of the app.
@property {Object} parameters - The parameters passed to your app (if any).
@property {HTMLElement} container - The container element of the app.
@property {Object} services - Some services provided by CMS (if any)
@property {function():string} ROOT_SELECTOR - The root CSS selector of the app, with a default value of "not set".
*/


export class YleInteractiveEmbed {
	appName = "";
	parameters = {};
	services = {};
	#installationDone = false;
	#initFunc = null;
	container = document.body;
	isLocalhostOrStandalone = Boolean(
	    window.location.hostname === "plus.yle.fi" ||
	    window.location.hostname === "lusi-dataviz.ylestatic.fi" ||
	    window.location.hostname === "lusi-dataviz.test.ylestatic.fi" ||
	    window.location.hostname === "svenska-dataviz.ylestatic.fi/" ||
	    window.location.hostname === "svenska-dataviz.test.ylestatic.fi/" ||
	    window.location.hostname === "dev.yle.fi" ||
	    window.location.hostname === "localhost" ||
	    // [::1] is the IPv6 localhost address.
	    window.location.hostname === "[::1]" ||
	    // 127.0.0.1/8 is considered localhost for IPv4.
	    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
	)
	/**
	* Creates an instance of the InteractiveApp class.
	* @param {string} NAME - The name of the app. REQUIRED!
	* @param {string} SELECTOR - The selector of root node. 
	*/
	constructor(NAME, SELECTOR) {
	    this.appName = NAME
	    if (SELECTOR) {
		this.USERDEFINED_ROOT_SELECTOR = SELECTOR;
	    }
	    this.#install();
	}
    
	get ROOT_SELECTOR() {
	    if (this.USERDEFINED_ROOT_SELECTOR) {
		return this.USERDEFINED_ROOT_SELECTOR
	    } else {
		return console.error("no root selector defined")
	    }
	}
    
    
	#getParametersFromParentNode(parent) {
	    let parameters = {};
	    if (parent.hasAttribute("data-yle-external-content-parameters")) {
		try {
		    parameters = JSON.parse(parent.getAttribute("data-yle-external-content-parameters"));
		} catch (e) {
		    console.log(e);
		}
	    }
	    return parameters;
	}
    
	#getParametersFromUrl() {
	    const parameters = {};
	    const searchParameters = new URLSearchParams(window.location.search);
	    for (const [key, value] of searchParameters) {
		parameters[key] = value;
	    }
	    return parameters;
	}
    
	#svenskaAndAiheHooks(appName, eventHandlers) {
	    window.yleVisualisationEmbeds = window.yleVisualisationEmbeds || {};
	    window.yleVisualisationEmbeds[appName] = eventHandlers;
	}
    
	#onMount(name, element, services) {
	    if (name !== this.appName) {
		return;
	    }
	    const containerNode = element.querySelector(this.ROOT_SELECTOR);
	    if (!containerNode) {
		console.error("no container found with selector " + this.ROOT_SELECTOR)
		return;
	    }
	    this.container = containerNode;
	    this.parameters = services.getParameters();
	    this.services = services;
	    if (this.#initFunc) this.#initFunc();
	    this.#installationDone = true;
	}
    
	#install() {
	    if (!this.appName) console.error("name is required");
	    const onMount = this.#onMount.bind(this);
    
	    const services = {};
	    switch (true) {
		case (!this.isLocalhostOrStandalone && typeof window.yleVisualisation === "object"):
		    this.#svenskaAndAiheHooks(this.appName, { onMount });
		    break;
		case (!this.isLocalhostOrStandalone && typeof window.yleVisualisation === "undefined"):
		    // Article renderer, main yle.fi site through Yddrasil
		    window.plusApp = window.plusApp || {};
		    const element = document.querySelector(`${this.ROOT_SELECTOR}:not([data-yle-vis-processed])`)
		    element.setAttribute("data-yle-vis-processed", this.appName);
		    const parent =
			element.closest("[data-yle-external-content-parameters]") ||
			element.parentElement ||
			document.body;
		    services.getParameters = () => this.#getParametersFromParentNode(parent);
		    onMount(this.appName, parent, services);
		    break;
		case this.isLocalhostOrStandalone:
		    // If the page is on localhost or standalone
		    services.getParameters = () => this.#getParametersFromUrl();
		    onMount(this.appName, document.body, services);
		    break;
		default:
		    console.log("no env");
	    }
	    return this;
	}
    
	#storeInitFunction(func) {
	    this.#initFunc = func;
	}
	/**
	Sets a callback function to be executed when the app is ready.
	If the app is already mounted and the installation is done, the callback function is executed immediately.
	If the app is not yet mounted, the callback function is stored and executed when the app is mounted.
	@function isReady
	@param {Function} cb - The callback function to be executed when the app is ready.
	*/
	set isReady(cb) {
	    if (this.#installationDone) {
		cb();
	    } else {
		this.#storeInitFunction(cb);
	    }
	}
    }